import React, { useEffect, useRef, useState } from "react";
import { ChevronRight, Images, X } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../api/api";
import gsap from "gsap";

const HERO_IMAGE = "/assets/images/herobg.jpeg";

const ImageGalleryPage = () => {
  const pageRef = useRef(null);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);
  const [preview, setPreview] = useState(null);

  const loadFolders = async () => {
    const res = await api.get("/gallery/folders?status=published");
    setFolders(res.data || []);
  };

  const loadFolder = async (slug) => {
    const res = await api.get(`/gallery/folders/${slug}`);
    setActiveFolder(res.data);
  };

  useEffect(() => {
    loadFolders();
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", { opacity: 0, y: 30, duration: 0.8 });
      gsap.from(".hero-title", { opacity: 0, y: 80, duration: 1.1 });
      gsap.from(".hero-text", { opacity: 0, y: 40, duration: 0.9, delay: 0.25 });
      gsap.from(".gallery-card", {
        opacity: 0,
        y: 50,
        duration: 0.9,
        stagger: 0.08,
      });

      gsap.to(".hero-orb-one", {
        y: 40,
        x: -25,
        repeat: -1,
        yoyo: true,
        duration: 6,
        ease: "sine.inOut",
      });

      gsap.to(".hero-orb-two", {
        y: -35,
        x: 25,
        repeat: -1,
        yoyo: true,
        duration: 7,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, [folders, activeFolder]);

  return (
    <main ref={pageRef}>
      <section className="relative overflow-hidden">
        <div className="relative min-h-[640px] bg-[#04160a] pt-28 pb-28 overflow-hidden">
          <img
            src={HERO_IMAGE}
            alt="AKSUBEB Image Gallery"
            className="absolute inset-0 w-full h-full object-cover"
          />

          <div className="absolute inset-0 bg-[#04160a]/80" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#04160a]/95 via-[#06183f]/75 to-[#ff6600]/35" />
          <div className="hero-orb-one absolute -top-24 -left-24 w-[460px] h-[460px] rounded-full bg-[#06183f]/35 blur-3xl" />
          <div className="hero-orb-two absolute -bottom-28 -right-24 w-[460px] h-[460px] rounded-full bg-[#ff6600]/30 blur-3xl" />
          <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:80px_80px]" />

          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <div className="hero-badge inline-flex items-center gap-3 mb-5">
              <span className="w-10 h-[3px] bg-[#ff6600]" />
              <span className="text-[#ff6600] font-bold tracking-[0.2em] text-sm uppercase">
                Media Gallery
              </span>
            </div>

            <h1
              className="hero-title text-5xl md:text-7xl lg:text-[82px] font-black text-white leading-[1.02]"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Image Gallery
            </h1>

            <p className="hero-text text-white/85 max-w-3xl mx-auto mt-6 leading-relaxed text-[17px]">
              Explore AKSUBEB photos arranged by events, projects, outreach,
              inspection visits and official occasions.
            </p>

            <div className="hero-text flex items-center justify-center gap-2 mt-6 text-sm text-white/80 flex-wrap">
              <Link to="/" className="hover:text-[#ff6600] transition-colors">
                Home
              </Link>
              <ChevronRight size={14} />
              <span className="text-[#ff6600]">Image Gallery</span>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-20">
          {!activeFolder && (
            <>
              <div className="text-center mb-12">
                <h2
                  className="text-4xl md:text-5xl font-black text-[#06183f]"
                  style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                >
                  Photo Folders
                </h2>

                <p className="text-slate-600 mt-3">
                  Select an occasion or folder to view its photos.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {folders.map((folder) => (
                  <button
                    key={folder.id}
                    onClick={() => loadFolder(folder.slug)}
                    className="gallery-card group text-left bg-white rounded-[30px] overflow-hidden border border-slate-100 shadow-[0_18px_45px_rgba(0,0,0,0.12)] hover:-translate-y-2 transition-all"
                  >
                    <div className="h-64 bg-slate-100 overflow-hidden">
                      {folder.cover_image ? (
                        <img
                          src={folder.cover_image}
                          alt={folder.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#06183f]">
                          <Images size={56} />
                        </div>
                      )}
                    </div>

                    <div className="p-6">
                      <h3 className="text-2xl font-black text-[#06183f]">
                        {folder.title}
                      </h3>

                      <p className="text-slate-600 mt-2 line-clamp-3">
                        {folder.description || "View photos from this occasion."}
                      </p>
                    </div>
                  </button>
                ))}
              </div>

              {!folders.length && (
                <p className="text-center text-slate-500">
                  No image folders available yet.
                </p>
              )}
            </>
          )}

          {activeFolder && (
            <>
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
                <div>
                  <button
                    onClick={() => setActiveFolder(null)}
                    className="text-[#ff6600] font-bold mb-3"
                  >
                    ← Back to folders
                  </button>

                  <h2
                    className="text-4xl md:text-5xl font-black text-[#06183f]"
                    style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                  >
                    {activeFolder.title}
                  </h2>

                  <p className="text-slate-600 mt-3 max-w-3xl">
                    {activeFolder.description}
                  </p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {activeFolder.images?.map((image) => (
                  <button
                    key={image.id}
                    onClick={() => setPreview(image)}
                    className="gallery-card group bg-white rounded-[26px] overflow-hidden border border-slate-100 shadow-[0_18px_45px_rgba(0,0,0,0.12)] text-left"
                  >
                    <div className="h-72 overflow-hidden bg-slate-100">
                      <img
                        src={image.image_url}
                        alt={image.alt || image.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                    </div>

                    <div className="p-5">
                      <h3 className="font-black text-[#06183f] text-xl">
                        {image.title}
                      </h3>

                      {image.description && (
                        <p className="text-slate-600 mt-2 line-clamp-2">
                          {image.description}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </div>

              {!activeFolder.images?.length && (
                <p className="text-slate-500">No images in this folder yet.</p>
              )}
            </>
          )}
        </div>
      </section>

      {preview && (
        <div className="fixed inset-0 z-[10000] bg-black/90 p-4 flex items-center justify-center">
          <button
            onClick={() => setPreview(null)}
            className="absolute top-5 right-5 w-12 h-12 rounded-full bg-white text-black flex items-center justify-center"
          >
            <X size={24} />
          </button>

          <div className="max-w-6xl w-full">
            <img
              src={preview.image_url}
              alt={preview.alt || preview.title}
              className="w-full max-h-[78vh] object-contain"
            />

            <div className="bg-white p-5">
              <h3 className="font-black text-[#06183f] text-2xl">
                {preview.title}
              </h3>

              {preview.description && (
                <p className="text-slate-600 mt-2">{preview.description}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default ImageGalleryPage;